/**
 * -----------------------------------------------------------------------------
 * NCJ COMPONENT: Multi-VIN Product Page Extension
 * FILE: ncj-product-multi-vin.js
 * PURPOSE: Handles dynamic VIN inputs based on quantity, validates, intercepts
 *          Add to Cart for line item splitting, and offers UPS Express addon toggle.
 *          Liquid-first: targets server-rendered elements for "Zero Flicker".
 *          JS enhances (not replaces) the Liquid-rendered components.
 * AUTHOR: No Code Jungle (NCJ)
 * LAST MODIFIED: 2026-03-06
 * -----------------------------------------------------------------------------
 */

document.addEventListener('DOMContentLoaded', function () {
    var productFormElement = document.querySelector('product-form');
    if (!productFormElement) return;

    var form = productFormElement.querySelector('form');
    if (!form) return;

    var formId = form.getAttribute('id');

    // The qty input is OUTSIDE the form but linked via form attribute (Dawn pattern)
    var qtyInput = null;
    if (formId) {
        qtyInput = document.querySelector('input[form="' + formId + '"].quantity__input');
    }
    if (!qtyInput) {
        qtyInput = document.querySelector('quantity-input .quantity__input');
    }
    if (!qtyInput) {
        qtyInput = form.querySelector('.quantity__input');
    }
    if (!qtyInput) return;

    // -------------------------------------------------------------------------
    // LIQUID-FIRST: Target the server-rendered VIN wrapper
    // -------------------------------------------------------------------------
    var vinContainer = form.querySelector('.ncj-product-vins-wrapper');
    if (!vinContainer) {
        // Fallback: create it dynamically (should not happen with Liquid-first)
        vinContainer = document.createElement('div');
        vinContainer.className = 'ncj-product-vins-wrapper';
    }

    // Attach listeners to the FIRST VIN field (already rendered by Liquid SSR)
    var firstVinField = vinContainer.querySelector('.ncj-vin-field-group');
    if (firstVinField) {
        attachVinFieldListeners(firstVinField);
    }

    // -------------------------------------------------------------------------
    // LIQUID-FIRST: Target the server-rendered Addon Toggle
    // Wire it into NCJUtils so getAddonState() works.
    // -------------------------------------------------------------------------
    var addonToggleRow = form.querySelector('.ncj-addon-toggle-row');
    var addonInput = null;

    if (addonToggleRow) {
        addonInput = addonToggleRow.querySelector('.addon-toggle-input, .ncj-addon-input');
        var titleEl = addonToggleRow.querySelector('[data-addon-toggle-title]');
        var subtitleEl = addonToggleRow.querySelector('[data-addon-toggle-subtitle]');

        if (addonInput && window.NCJUtils) {
            // Disable until addon product is fetched
            addonInput.disabled = true;

            // Load addon product to get its variant ID + price
            window.NCJUtils.loadAddonProduct().then(function (result) {
                if (!result || !result.variantId) {
                    if (subtitleEl) subtitleEl.textContent = "No disponible";
                    return;
                }
                addonInput.disabled = false;
                if (window.NCJUtils._updateAddonLabel && titleEl) {
                    window.NCJUtils._updateAddonLabel(addonInput, titleEl, result.priceText);
                }
            });

            // Wire change listener to update label
            addonInput.addEventListener('change', function () {
                window.NCJUtils.loadAddonProduct().then(function (result) {
                    if (result && window.NCJUtils._updateAddonLabel && titleEl) {
                        window.NCJUtils._updateAddonLabel(addonInput, titleEl, result.priceText);
                    }
                });
            });

            // Subtitle click toggles checkbox
            if (subtitleEl) {
                subtitleEl.addEventListener('click', function () {
                    if (addonInput.disabled) return;
                    addonInput.checked = !addonInput.checked;
                    addonInput.dispatchEvent(new Event('change'));
                });
            }

            // Register as the active addon input in NCJUtils
            if (typeof window.NCJUtils.setActiveAddonInput === 'function') {
                window.NCJUtils.setActiveAddonInput(addonInput);
            }
        }
    }

    // -------------------------------------------------------------------------
    // Variant change: hide/show toggle for PDF-only variants
    // -------------------------------------------------------------------------
    if (addonToggleRow && addonInput) {
        syncAddonWithVariant();
        var variantContainer = document.querySelector('variant-radios') || document.querySelector('variant-selects');
        if (variantContainer) {
            variantContainer.addEventListener('change', function () {
                setTimeout(syncAddonWithVariant, 150);
            });
        }

        var variantIdInput = form.querySelector('[name="id"]');
        if (variantIdInput) {
            variantIdInput.addEventListener('change', syncAddonWithVariant);
        }
    }

    function syncAddonWithVariant() {
        if (!addonToggleRow || !addonInput) return;

        // Read SKU from Dawn's SKU display element
        var skuElement = document.querySelector('[id^="Sku-"]');
        var variantSku = skuElement ? skuElement.textContent.trim() : '';

        // Read variant title from selected radio buttons
        var variantTitle = '';
        var variantButtons = document.querySelectorAll('.product-form__input input[type="radio"]:checked');
        if (variantButtons.length > 0) {
            variantTitle = Array.from(variantButtons).map(function (b) { return b.value; }).join(' ');
        }

        // Hide if explicitly a PDF-only variant
        var isPdfOnly = variantSku.indexOf('-2') !== -1
            || variantTitle.toLowerCase().indexOf('pdf only') !== -1
            || (variantTitle.toLowerCase().indexOf('pdf') !== -1 && variantTitle.toLowerCase().indexOf('original') === -1);

        if (isPdfOnly) {
            addonToggleRow.style.visibility = 'hidden';
            addonToggleRow.style.pointerEvents = 'none';
            addonInput.checked = false;
        } else {
            addonToggleRow.style.visibility = 'visible';
            addonToggleRow.style.pointerEvents = 'auto';
        }
    }

    // -------------------------------------------------------------------------
    // Quantity tracking & VIN field expansion
    // "NCJ Owns the Count" — ncjVinCount is our source of truth,
    // NOT Dawn's input.value (which gets reset to 1 on variant change).
    // -------------------------------------------------------------------------
    var maxQty = 10;
    var currentQty = parseInt(qtyInput.value) || 1;
    var ncjVinCount = currentQty; // Our independent VIN count
    var isVariantChanging = false;

    var btnMinus = document.querySelector('quantity-input button[name="minus"]');
    var btnPlus = document.querySelector('quantity-input button[name="plus"]');

    function updateButtonStates(qty) {
        if (btnPlus) {
            if (qty >= maxQty) {
                btnPlus.setAttribute('disabled', 'disabled');
                btnPlus.classList.add('disabled');
                btnPlus.style.cursor = 'not-allowed';
            } else {
                btnPlus.removeAttribute('disabled');
                btnPlus.classList.remove('disabled');
                btnPlus.style.cursor = 'pointer';
            }
        }
        if (btnMinus) {
            if (qty <= 1) {
                btnMinus.setAttribute('disabled', 'disabled');
                btnMinus.classList.add('disabled');
                btnMinus.style.cursor = 'not-allowed';
            } else {
                btnMinus.removeAttribute('disabled');
                btnMinus.classList.remove('disabled');
                btnMinus.style.cursor = 'pointer';
            }
        }
    }

    updateButtonStates(ncjVinCount);
    renderVinInputs(ncjVinCount);

    // -------------------------------------------------------------------------
    // CLICK-BASED detection on Dawn's +/- buttons
    // We listen for clicks (user actions), NOT value changes (programmatic).
    // Dawn updates .value before our listener runs, so we read the new value.
    // -------------------------------------------------------------------------
    if (btnPlus) {
        btnPlus.addEventListener('click', function () {
            // Micro-delay: Dawn's quantity-input component increments
            // the value synchronously on click, but we wait one tick
            // to guarantee the value is already updated.
            setTimeout(function () {
                var newVal = parseInt(qtyInput.value) || 1;
                if (newVal > maxQty) { newVal = maxQty; qtyInput.value = maxQty; }
                if (newVal !== ncjVinCount) {
                    ncjVinCount = newVal;
                    currentQty = newVal;
                    updateButtonStates(ncjVinCount);
                    renderVinInputs(ncjVinCount);
                }
            }, 50);
        });
    }

    if (btnMinus) {
        btnMinus.addEventListener('click', function () {
            setTimeout(function () {
                var newVal = parseInt(qtyInput.value) || 1;
                if (newVal < 1) { newVal = 1; qtyInput.value = 1; }
                if (newVal !== ncjVinCount) {
                    ncjVinCount = newVal;
                    currentQty = newVal;
                    updateButtonStates(ncjVinCount);
                    renderVinInputs(ncjVinCount);
                }
            }, 50);
        });
    }

    // Direct typing in the input field (user action, not programmatic)
    qtyInput.addEventListener('change', handleQtyChange);
    qtyInput.addEventListener('input', handleQtyChange);

    function handleQtyChange() {
        if (isVariantChanging) return; // Ignore Dawn's programmatic reset
        var newQty = parseInt(qtyInput.value) || 1;
        if (newQty > maxQty) { newQty = maxQty; qtyInput.value = maxQty; }
        if (newQty < 1) { newQty = 1; qtyInput.value = 1; }
        if (newQty !== ncjVinCount) {
            ncjVinCount = newQty;
            currentQty = newQty;
            updateButtonStates(ncjVinCount);
            renderVinInputs(ncjVinCount);
        }
    }

    // -------------------------------------------------------------------------
    // VARIANT CHANGE: Block Dawn's reset at the source.
    // Dawn's setQuantityBoundries() does: this.input.value = min (= 1)
    // We override the .value setter on this specific input so that during
    // a variant change, Dawn's write is silently blocked.
    // -------------------------------------------------------------------------
    var variantContainer = document.querySelector('variant-radios') || document.querySelector('variant-selects');
    if (variantContainer) {
        variantContainer.addEventListener('change', function () {
            isVariantChanging = true;
        });
    }

    // Override the .value property on the qty input to guard against resets
    var originalValueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    Object.defineProperty(qtyInput, 'value', {
        get: function () {
            return originalValueDescriptor.get.call(this);
        },
        set: function (newVal) {
            if (isVariantChanging && parseInt(newVal) < ncjVinCount) {
                // Dawn is trying to reset to min — block silently
                return;
            }
            originalValueDescriptor.set.call(this, newVal);
        },
        configurable: true
    });

    // Release the lock after Dawn's full async cycle completes
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        subscribe(window.PUB_SUB_EVENTS.variantChange, function (event) {
            var sectionEl = productFormElement.closest('section');
            if (!sectionEl) { isVariantChanging = false; return; }
            var sectionId = sectionEl.id.replace('shopify-section-', '');
            if (event.data.sectionId !== sectionId) { isVariantChanging = false; return; }

            // By the time this fires, Dawn's setQuantityBoundries() already
            // tried to set value=1 but our guard blocked it.
            // Just release the lock.
            isVariantChanging = false;
        });
    }

    // -------------------------------------------------------------------------
    // VIN Field Rendering — uses the global <template> from ncj-vin-field.liquid
    // The first field is SSR (Liquid), subsequent fields are cloned from template
    // -------------------------------------------------------------------------
    function renderVinInputs(qty) {
        var existingInputs = vinContainer.querySelectorAll('.ncj-vin-field-group');
        var existingCount = existingInputs.length;

        if (qty > existingCount) {
            // Clone from template (shared with cart via ncj-utils)
            var template = document.getElementById('ncj-vin-field-template');
            if (!template && window.NCJUtils && window.NCJUtils.createVinFieldGroup) {
                // Fallback to NCJUtils if template not found
                for (var i = existingCount + 1; i <= qty; i++) {
                    var group = window.NCJUtils.createVinFieldGroup({
                        label: (window.vinCheckTranslations && window.vinCheckTranslations.vin || 'VIN') + ' ' + i,
                        id: 'ncj-vin-' + i,
                        showIcon: true,
                        customClasses: 'ncj-vin-full-width'
                    });
                    vinContainer.appendChild(group);
                }
                return;
            }
            if (!template) return;

            for (var i = existingCount + 1; i <= qty; i++) {
                var clone = template.content.cloneNode(true);
                var group = clone.querySelector('.ncj-vin-field-group');
                var input = clone.querySelector('.ncj-vin-input');
                if (group) group.setAttribute('data-vin-index', i);
                if (input) {
                    input.setAttribute('name', 'properties[VIN ' + i + ']');
                    input.setAttribute('id', 'ncj-vin-' + i);
                }

                vinContainer.appendChild(clone);

                var newGroup = vinContainer.querySelector('.ncj-vin-field-group[data-vin-index="' + i + '"]');
                if (newGroup) attachVinFieldListeners(newGroup);
            }
        } else if (qty < existingCount) {
            for (var j = existingCount; j > qty; j--) {
                var fieldToRemove = vinContainer.querySelector('.ncj-vin-field-group[data-vin-index="' + j + '"]');
                if (fieldToRemove) {
                    fieldToRemove.remove();
                } else if (vinContainer.lastElementChild) {
                    vinContainer.removeChild(vinContainer.lastElementChild);
                }
            }
        }
    }

    // -------------------------------------------------------------------------
    // VIN Field Listeners (normalization + validation)
    // -------------------------------------------------------------------------
    function attachVinFieldListeners(group) {
        var input = group.querySelector('.ncj-vin-input');
        var counter = group.querySelector('.ncj-vin-char-counter');
        var error = group.querySelector('.ncj-vin-error');

        if (!input) return;

        input.addEventListener('input', function (e) {
            var val = e.target.value;
            if (window.NCJUtils && window.NCJUtils.normalizeVin) {
                var normalized = window.NCJUtils.normalizeVin(val);
                if (val !== normalized) e.target.value = normalized;
                val = normalized;
            }

            if (counter) {
                counter.textContent = val.length + '/17';
                if (val.length > 0 && val.length < 17) {
                    counter.classList.add('invalid-length');
                } else {
                    counter.classList.remove('invalid-length');
                }
            }
            if (error && val.length === 17) error.style.display = 'none';
        });

        input.addEventListener('blur', function () {
            if (input.value && input.value.length < 17) {
                if (error) {
                    error.textContent = (window.vinCheckTranslations && window.vinCheckTranslations.exactLength) || 'Must be 17 characters';
                    error.style.display = 'block';
                }
            }
        });
    }

    // -------------------------------------------------------------------------
    // Form Submit Intercept — Capture phase to run BEFORE Dawn's product-form
    // -------------------------------------------------------------------------
    form.addEventListener('submit', handleFormSubmit, { capture: true });

    function handleFormSubmit(e) {
        if (!window.NCJUtils || !window.NCJUtils.validateVinContainer) return;

        var validation = window.NCJUtils.validateVinContainer(vinContainer);
        if (!validation.isValid) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
        }

        var vins = validation.vins;

        e.preventDefault();
        e.stopImmediatePropagation();

        var variantIdInput = form.querySelector('[name="id"]');
        var variantId = variantIdInput ? variantIdInput.value : null;
        if (!variantId) return;

        // Collect other form properties (skip NCJ VIN inputs)
        var customProperties = {};
        var hiddenProps = form.querySelectorAll('input[name^="properties["]:not(.ncj-vin-input)');
        hiddenProps.forEach(function (prop) {
            var propNameMatch = prop.name.match(/properties\[(.*?)\]/);
            if (propNameMatch && propNameMatch[1]) {
                customProperties[propNameMatch[1]] = prop.value;
            }
        });

        // Build the items array
        var items = [];

        // 1. UPS Express addon (separate line item)
        var addonState = window.NCJUtils && window.NCJUtils.getAddonState
            ? window.NCJUtils.getAddonState()
            : null;
        if (addonState && addonState.checked && addonState.variantId) {
            var addonProps = {};
            if (addonState.vendor) {
                addonProps["Brand"] = addonState.vendor;
            }
            items.push({
                id: parseInt(addonState.variantId),
                quantity: 1,
                properties: addonProps
            });
        }

        // 2. Each COC with its unique VIN (reversed for correct cart display)
        var reversedVins = vins.slice().reverse();
        reversedVins.forEach(function (vin) {
            items.push({
                id: parseInt(variantId),
                quantity: 1,
                properties: Object.assign({}, customProperties, { "VIN": vin })
            });
        });

        // UI Loading state
        var submitButton = productFormElement.querySelector('[type="submit"]');
        if (submitButton) {
            submitButton.setAttribute('aria-disabled', true);
            submitButton.classList.add('loading');
            var spinner = productFormElement.querySelector('.loading__spinner');
            if (spinner) spinner.classList.remove('hidden');
        }

        // Build request body with sections support for Dawn cart-drawer
        var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        var body = { items: items };

        if (cart && typeof cart.getSectionsToRender === 'function') {
            body.sections = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
            body.sections_url = window.location.pathname;
            if (typeof cart.setActiveElement === 'function') {
                cart.setActiveElement(document.activeElement);
            }
        }

        fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(body)
        })
            .then(function (response) {
                return response.text();
            })
            .then(function (text) {
                // Defensive check: If Shopify rate limits or bot-protects, it returns HTML
                if (text.trim().indexOf("<!DOCTYPE") === 0 || text.trim().indexOf("<html") === 0) {
                    throw new Error("Temporary block by Shopify bot protection. Please refresh the page.");
                }
                try {
                    return JSON.parse(text);
                } catch (e) {
                    throw new Error("An unexpected error occurred. Please try again.");
                }
            })
            .then(function (data) {
                if (data.status && data.status >= 400) {
                    if (typeof productFormElement.handleErrorMessage === 'function') {
                        productFormElement.handleErrorMessage(data.description || data.message);
                    }
                    return;
                }

                if (cart && typeof cart.renderContents === 'function') {
                    cart.renderContents(data);
                } else {
                    window.location = window.routes.cart_url;
                }

                // Publish PubSub event for Dawn's other components
                if (window.publish && window.PUB_SUB_EVENTS) {
                    window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                        source: 'product-form',
                        productVariantId: variantId,
                        cartData: data
                    });
                }
            })
            .catch(function (error) {
                if (typeof productFormElement.handleErrorMessage === 'function') {
                    productFormElement.handleErrorMessage(error.message);
                }
            })
            .finally(function () {
                if (submitButton) {
                    submitButton.classList.remove('loading');
                    submitButton.removeAttribute('aria-disabled');
                    var spinner = productFormElement.querySelector('.loading__spinner');
                    if (spinner) spinner.classList.add('hidden');
                }
            });
    }
});
